import { useEffect, useState } from "react";
import {
    FaShoppingCart,
    FaUserCheck,
    FaWarehouse,
    FaFileInvoice,
    FaTruck,
    FaBoxOpen,
    FaFileInvoiceDollar,
} from "react-icons/fa";
import { API_BASE } from "../utils/api";
import { useLanguage } from "../context/LanguageContext";

const STATUS_STEPS = [
    { key: "EC_HECHO", icon: FaShoppingCart },
    { key: "EC_APROBADO", icon: FaUserCheck },
    { key: "ULTRA_RECIBIDO", icon: FaWarehouse },
    { key: "ULTRA_GUIA_EMITIDA", icon: FaFileInvoice },
    { key: "ULTRA_EN_RUTA", icon: FaTruck },
    { key: "EC_ENTREGADO", icon: FaBoxOpen },
    { key: "ULTRA_FACTURADO", icon: FaFileInvoiceDollar },
];

const CIRCLE_SIZE = 30;

function StatusRow({ label, value }) {
    return (
        <p className="flex items-baseline gap-2 text-left">
            <span className="text-gray-400 w-16 flex-shrink-0">{label}</span>
            <span className="font-medium text-white">{value}</span>
        </p>
    );
}

export default function OrderStatusTracker({ orderId, accentColor = "#16a34a", onOpenRide }) {
    const { t } = useLanguage();
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!orderId) return;
        let cancelled = false;

        const fetchStatus = async () => {
            try {
                setLoading(true);
                const res = await fetch(
                    `${API_BASE}/orders-ultra/api/ws/list_status_order?order_id=${orderId}`
                );
                const data = await res.json();
                if (!cancelled) setRecords(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error("Error cargando tracking de la orden:", err);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchStatus();
        return () => {
            cancelled = true;
        };
    }, [orderId]);

    if (loading) {
        return (
            <div className="mt-4 bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3 animate-pulse">
                <div className="h-3 w-40 bg-gray-200 rounded mb-2" />
                <div className="h-9 bg-gray-100 rounded-full" />
            </div>
        );
    }

    if (!records.length) return null;

    // La guía y la factura solo cuentan como alcanzadas si el SRI ya las autorizó (newOrderId presente)
    const validRecords = records.filter((r) => {
        if (r.statusEnum === "ULTRA_GUIA_EMITIDA" || r.statusEnum === "ULTRA_FACTURADO") {
            return !!r.newOrderId;
        }
        return true;
    });

    const recordsByKey = {};
    validRecords.forEach((r) => {
        const existing = recordsByKey[r.statusEnum];
        if (!existing || new Date(r.statusDay) > new Date(existing.statusDay)) {
            recordsByKey[r.statusEnum] = r;
        }
    });

    const achievedIndexes = validRecords
        .map((r) => STATUS_STEPS.findIndex((s) => s.key === r.statusEnum))
        .filter((i) => i >= 0);
    const currentIndex = achievedIndexes.length ? Math.max(...achievedIndexes) : 0;

    const formatFullDate = (d) =>
        new Date(d).toLocaleString("es-ES", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });

    return (
        <div className="mt-4 bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3">
            <h3 className="text-sm font-bold uppercase tracking-wide text-gray-700 mb-2">
                {t.orderStatusTitle}
            </h3>
            <div>
                <div className="flex">
                    {STATUS_STEPS.map((step, idx) => {
                        const record = recordsByKey[step.key];
                        const isDone = idx < currentIndex;
                        const isCurrent = idx === currentIndex;
                        const Icon = step.icon;

                        const circleStyle = isDone
                            ? { backgroundColor: accentColor, borderColor: accentColor, color: "#fff" }
                            : isCurrent
                            ? { backgroundColor: "#fff", borderColor: accentColor, color: accentColor, boxShadow: `0 0 0 4px ${accentColor}22` }
                            : { backgroundColor: "#f3f4f6", borderColor: "#d1d5db", color: "#6b7280" };

                        const rideOrderId =
                            step.key === "ULTRA_GUIA_EMITIDA" || step.key === "ULTRA_FACTURADO"
                                ? record?.newOrderId
                                : step.key === "EC_APROBADO"
                                ? (record ? orderId : null)
                                : step.key === "EC_HECHO"
                                ? orderId
                                : null;
                        const isRideStep = !!rideOrderId && !!onOpenRide;

                        const driverInfo =
                            step.key === "ULTRA_EN_RUTA" && record?.notes
                                ? record.notes.split("|").map((s) => s.trim())
                                : null;
                        const [driverName, driverVehicle, driverPlate] = driverInfo || [];
                        const recipientName =
                            step.key === "EC_ENTREGADO" && record?.notes ? record.notes : null;
                        const handleStepClick = () => {
                            if (!isRideStep) return;
                            onOpenRide(
                                `${API_BASE}/orders-ultra/api/ws/generate_ride_order?order_id=${rideOrderId}`
                            );
                        };

                        return (
                            <div key={step.key} className="relative flex-1 flex flex-col items-center">
                                {idx > 0 && (
                                    <div
                                        className="absolute h-0.5 transition-colors"
                                        style={{
                                            top: CIRCLE_SIZE / 2,
                                            right: "50%",
                                            width: "100%",
                                            backgroundColor: idx <= currentIndex ? accentColor : "#e5e7eb",
                                            zIndex: 0,
                                        }}
                                    />
                                )}

                                <div
                                    className={`group relative z-8 flex flex-col items-center ${isRideStep ? "cursor-pointer" : "cursor-default"}`}
                                    onClick={handleStepClick}
                                >
                                    <div
                                        className="rounded-full border-2 flex items-center justify-center transition-all duration-200"
                                        style={{ width: CIRCLE_SIZE, height: CIRCLE_SIZE, ...circleStyle }}
                                    >
                                        <Icon size={12} />
                                    </div>
                                    <span
                                        className="mt-1 text-sm font-bold text-center leading-tight px-1"
                                        style={{ color: isDone || isCurrent ? "#111827" : "#6b7280" }}
                                    >
                                        {t[`status_${step.key}_label`]}
                                    </span>
                                    {record?.statusDay && (
                                        <span className="text-xs font-medium text-gray-500 text-center">
                                            {formatFullDate(record.statusDay)}
                                        </span>
                                    )}

                                    <div className="pointer-events-none absolute bottom-full mb-2 left-1/2 -translate-x-1/2 w-80 bg-gray-900 text-white text-xs rounded-lg py-2 px-2.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-30 shadow-lg">
                                        <p className="font-semibold text-left mb-2">
                                            {t[`status_${step.key}_desc`] || record?.statusDescription}
                                        </p>

                                        {record?.statusDay || record?.nameEmployee || driverInfo || recipientName ? (
                                            <div className="space-y-1 pt-2 border-t border-gray-700/80">
                                                {driverName && <StatusRow label="Conductor" value={driverName} />}
                                                {driverVehicle && <StatusRow label="Vehículo" value={driverVehicle} />}
                                                {driverPlate && <StatusRow label="Placa" value={driverPlate} />}
                                                {recipientName && <StatusRow label="Recibido por" value={recipientName} />}
                                                {record?.nameEmployee && (
                                                    <StatusRow
                                                        label={step.key === "ULTRA_EN_RUTA" ? "Autoriza" : "Responsable"}
                                                        value={record.nameEmployee}
                                                    />
                                                )}
                                                {record?.statusDay && (
                                                    <StatusRow label="Fecha" value={formatFullDate(record.statusDay)} />
                                                )}
                                            </div>
                                        ) : (
                                            <p className="text-gray-400">{t.orderStatusPending}</p>
                                        )}
                                        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
